"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  ChevronDown,
  ChevronUp,
  Info,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import {
  YILI,
  SEKTOR_ORTALAMALARI,
  hesaplaIseAlimSuresi,
  type IseAlimSonuc,
} from "./ise-alim-engine";

const howToSchema = generateHowToSchema({
  title: "Time-to-Hire (İşe Alım Süresi) Nasıl Hesaplanır?",
  description:
    "İş ilanı yayınlanmasından adayın işe başlamasına kadar geçen süreyi hesaplayın. Sektör ortalamasıyla karşılaştırın.",
  totalTime: "PT2M",
  steps: [
    {
      name: "İlan Yayınlanma Tarihini Girin",
      text: "İş ilanının yayınlandığı veya pozisyon açıldığı tarihi seçin.",
    },
    {
      name: "İşe Başlama Tarihini Girin",
      text: "Adayın şirkette fiilen işe başladığı tarihi seçin.",
    },
    {
      name: "Sektörü Seçin",
      text: "Sektör benchmarkıyla karşılaştırma yapabilmek için sektörünüzü seçin.",
    },
  ],
});

function SSSSoru({ soru, cevap }: { soru: string; cevap: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

const RENK_SINIF: Record<string, Record<string, string>> = {
  emerald: {
    bg: "bg-emerald-500",
    text: "text-emerald-600",
    bgLight: "bg-emerald-50",
    border: "border-emerald-200",
  },
  blue: {
    bg: "bg-blue-500",
    text: "text-blue-600",
    bgLight: "bg-blue-50",
    border: "border-blue-200",
  },
  amber: {
    bg: "bg-amber-500",
    text: "text-amber-600",
    bgLight: "bg-amber-50",
    border: "border-amber-200",
  },
  red: {
    bg: "bg-red-500",
    text: "text-red-600",
    bgLight: "bg-red-50",
    border: "border-red-200",
  },
};

export default function IseAlimSuresiClient() {
  const [ilanTarihi, setIlanTarihi] = useState("");
  const [iseBaslamaTarihi, setIseBaslamaTarihi] = useState("");
  const [sektor, setSektor] = useState("Genel Ortalama");
  const [sonuc, setSonuc] = useState<IseAlimSonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const hesapla = useCallback(() => {
    setHata(null);
    if (!ilanTarihi || !iseBaslamaTarihi) {
      setHata("Lütfen her iki tarihi de giriniz.");
      setSonuc(null);
      return;
    }
    if (new Date(iseBaslamaTarihi) < new Date(ilanTarihi)) {
      setHata("İşe başlama tarihi, ilan tarihinden sonra olmalıdır.");
      setSonuc(null);
      return;
    }
    const result = hesaplaIseAlimSuresi({
      ilanTarihi,
      iseBaslamaTarihi,
      sektor,
    });
    setSonuc(result);
  }, [ilanTarihi, iseBaslamaTarihi, sektor]);

  const renkler = sonuc ? RENK_SINIF[sonuc.renk] || RENK_SINIF.blue : null;

  return (
    <>
      <StructuredData data={[howToSchema]} />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-purple-500 to-violet-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "İşe Alım Süresi",
                  href: "/araclar/ise-alim-suresi-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />
            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <Clock className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  İşe Alım Süresi Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  {YILI} Sektör Ortalamaları
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              Time-to-Hire metriğinizi hesaplayın ve {YILI} Türkiye sektör
              ortalamalarıyla karşılaştırın.
            </p>
          </div>
        </section>

        {/* Hesaplama */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* Sektör Benchmark Tablosu */}
              <div className="mb-8 bg-purple-50 rounded-xl p-5 border border-purple-100">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  Sektör Ortalamaları ({YILI} Türkiye)
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {SEKTOR_ORTALAMALARI.filter(
                    (s) => s.sektor !== "Genel Ortalama"
                  ).map((s) => (
                    <div
                      key={s.sektor}
                      className="bg-white rounded-lg p-3 text-center"
                    >
                      <span className="text-xl font-bold text-purple-600 block">
                        {s.ortalamaGun}
                      </span>
                      <span className="text-xs text-gray-500">{s.sektor}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Girdiler */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="ilanTarihi"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    İlan Yayınlanma Tarihi
                  </label>
                  <input
                    id="ilanTarihi"
                    type="date"
                    value={ilanTarihi}
                    onChange={(e) => setIlanTarihi(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-900"
                  />
                </div>
                <div>
                  <label
                    htmlFor="iseBaslamaTarihi"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    İşe Başlama Tarihi
                  </label>
                  <input
                    id="iseBaslamaTarihi"
                    type="date"
                    value={iseBaslamaTarihi}
                    onChange={(e) => setIseBaslamaTarihi(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-900"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="sektor"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Sektör
                  </label>
                  <select
                    id="sektor"
                    value={sektor}
                    onChange={(e) => setSektor(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-900"
                  >
                    {SEKTOR_ORTALAMALARI.map((s) => (
                      <option key={s.sektor} value={s.sektor}>
                        {s.sektor} (Ort. {s.ortalamaGun} gün)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {hata && (
                <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{hata}</p>
                </div>
              )}

              <button
                type="button"
                onClick={hesapla}
                className="w-full bg-purple-600 text-white py-4 rounded-xl font-semibold hover:bg-purple-700 active:bg-purple-800 transition-colors shadow-sm"
              >
                <span className="flex items-center justify-center gap-2">
                  <Clock className="w-5 h-5" />
                  Hesapla
                </span>
              </button>
            </div>

            {/* Sonuç */}
            {sonuc && renkler && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-purple-600 text-white rounded-xl p-4 text-center">
                    <span className="text-xs text-purple-100 block">
                      Time-to-Hire
                    </span>
                    <span className="text-3xl font-bold block tabular-nums">
                      {sonuc.gun}
                    </span>
                    <span className="text-xs text-purple-100">Gün</span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                    <span className="text-xs text-gray-500 block">Süre</span>
                    <span className="text-2xl font-bold text-gray-900 block tabular-nums">
                      {sonuc.hafta}
                    </span>
                    <span className="text-xs text-gray-500">Hafta</span>
                  </div>
                  <div
                    className={`${renkler.bgLight} rounded-xl p-4 text-center border ${renkler.border}`}
                  >
                    <span className="text-xs text-gray-500 block">
                      Değerlendirme
                    </span>
                    <span
                      className={`text-lg font-bold ${renkler.text} block mt-1`}
                    >
                      {sonuc.degerlendirme}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                    <span className="text-xs text-gray-500 block">
                      Sektör Ort.
                    </span>
                    <span className="text-2xl font-bold text-gray-900 block tabular-nums">
                      {sonuc.sektorOrtalamaGun}
                    </span>
                    <span className="text-xs text-gray-500">Gün</span>
                  </div>
                </div>

                {/* Benchmark Bar */}
                <div className="bg-white rounded-xl p-5 border border-gray-200 mb-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-2">
                    <span>0 gün</span>
                    <span>Ort. ({sonuc.sektorOrtalamaGun} gün)</span>
                    <span>60+ gün</span>
                  </div>
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${renkler.bg} rounded-full transition-all`}
                      style={{
                        width: `${Math.min((sonuc.gun / 60) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-sm mt-3">
                    <span className="text-gray-600">Fark:</span>
                    <span
                      className={`font-bold ${sonuc.farkGun <= 0 ? "text-emerald-600" : "text-red-500"}`}
                    >
                      {sonuc.farkGun <= 0 ? "" : "+"}
                      {sonuc.farkGun} gün ({sonuc.farkYuzde >= 0 ? "+" : ""}
                      {sonuc.farkYuzde.toFixed(0)}%)
                    </span>
                  </div>
                </div>

                {/* Öneriler */}
                <div
                  className={`${renkler.bgLight} rounded-xl p-5 border ${renkler.border}`}
                >
                  <div className="flex items-start gap-3">
                    <TrendingUp
                      className={`w-5 h-5 ${renkler.text} shrink-0 mt-0.5`}
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {sonuc.degerlendirme === "Mükemmel"
                          ? "Tebrikler! İşe alım süreciniz çok verimli."
                          : "İyileştirme Önerileri"}
                      </h3>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {sonuc.oneriler.map((oneri) => (
                          <li key={oneri}>• {oneri}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* SSS */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Sık Sorulan Sorular
          </h2>
          <div className="space-y-3">
            <SSSSoru
              soru="Time-to-Hire nedir?"
              cevap="Time-to-Hire (işe alım süresi), iş ilanının yayınlanmasından adayın işe başlamasına kadar geçen süreyi ölçen kritik bir İK metriğidir. İşe alım sürecinin verimliliğini değerlendirmek için kullanılır."
            />
            <SSSSoru
              soru="Time-to-Hire ile Time-to-Fill arasındaki fark nedir?"
              cevap="Time-to-Hire, ilan yayınlanmasından işe başlamaya kadar olan süreyi ölçerken, Time-to-Fill pozisyon açılış kararından işe başlamaya kadar olan süreyi ölçer. Time-to-Fill, pozisyon planlama süresini de içerir."
            />
            <SSSSoru
              soru="İdeal Time-to-Hire süresi kaç gündür?"
              cevap="İdeal süre sektöre göre değişir. Genel olarak 21 günün altı mükemmel, 30-45 gün ortalama, 45+ gün iyileştirme gerektirir. Teknoloji sektöründe 35-50 gün normal kabul edilir."
            />
            <SSSSoru
              soru="Time-to-Hire nasıl iyileştirilir?"
              cevap="AI destekli CV analizi ile ön eleme süresini kısaltma, standart mülakat süreçleri oluşturma, hızlı karar mekanizmaları kurma ve otomatik bildirim sistemleri kullanma en etkili yöntemlerdir."
            />
            <SSSSoru
              soru="Time-to-Hire maliyeti nasıl etkiler?"
              cevap="Uzun time-to-hire, açık pozisyon maliyetlerini artırır. Ayrıca, uzun süreçler en iyi adayların başka şirketlere gitmesine neden olur. Hızlı işe alım, hem maliyet hem de yetenek kaybını önler."
            />
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="p-5 bg-purple-50 rounded-xl border border-purple-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-600">
                Sektör ortalamaları {YILI} Türkiye verilerine dayalıdır.
                Time-to-Hire, takvim günü olarak hesaplanır.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="ise-alim-suresi-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-violet-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              İşe Alım Sürenizi %70 Kısaltın
            </h2>
            <p className="text-violet-100 mb-6 max-w-xl mx-auto">
              IKAI ile AI destekli CV analizi, otomatik aday sıralama ve entegre
              mülakat planlama.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-violet-700 px-6 py-3 rounded-xl font-semibold hover:bg-violet-50 transition-colors"
            >
              Ücretsiz Deneyin
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
